'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';

import useTrivia from '@/hooks/useTrivia';
import ROUTES from '@/router/routes';
import { initDashboardTrivia, participateTrivia } from '@/services/dashboard';
import type { ResponseDashTriviaInit } from '@/types/dashboard';

interface UseTriviaProps {
  isMobile?: boolean;
}

export const useTriviaSection = ({ isMobile = false }: UseTriviaProps = {}) => {
  const [step, setStep] = useState(0);
  const [showStats, setShowStats] = useState(false);
  const [totalStats, setTotalStats] = useState(false);
  const [cameFromInit, setCameFromInit] = useState(false);
  const [previousStep, setPreviousStep] = useState<number>(0);
  const [previousTotalStats, setPreviousTotalStats] = useState<boolean>(false);
  const navigate = useNavigate();

  const { triviaData } = useTrivia();

  const {
    data: triviaInit,
    isLoading,
    isRefetching,
    refetch,
  } = useQuery<ResponseDashTriviaInit>(
    'init-trivia',
    async () => initDashboardTrivia(),
    {
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        if (!data.hayTriviaPendiente && !data.hayTriviaActiva) {
          // Finalizaron
          return setStep(3);
        }

        if (
          data.yaParticipa ||
          (Boolean(data.trv) &&
            Boolean(data.resultadoParticipacion) &&
            data.trv?.nombre ===
              data.resultadoParticipacion?.nombreTriviaDesencadenante) ||
          (!data.hayTriviaActiva &&
            Boolean(data.trvAnt) &&
            Boolean(data.resultadoParticipacion) &&
            data.trvAnt?.nombre ===
              data.resultadoParticipacion?.nombreTriviaDesencadenante)
        ) {
          // ir a resultados
          return setStep(2);
        }
      },
    }
  );

  const shouldShowResult = useMemo(() => {
    return (
      triviaInit?.yaParticipa ||
      (Boolean(triviaInit?.trv) &&
        Boolean(triviaInit?.resultadoParticipacion) &&
        triviaInit?.trv?.nombre ===
          triviaInit?.resultadoParticipacion?.nombreTriviaDesencadenante) ||
      (!triviaInit?.hayTriviaActiva &&
        Boolean(triviaInit?.trvAnt) &&
        Boolean(triviaInit?.resultadoParticipacion) &&
        triviaInit?.trvAnt?.nombre ===
          triviaInit?.resultadoParticipacion?.nombreTriviaDesencadenante)
    );
  }, [triviaInit]);

  const handleInitQuestions = useCallback(async () => {
    await participateTrivia();
    setStep(1);
  }, []);

  const handleRefetch = useCallback(async () => {
    await refetch();
    setStep(2);
  }, [refetch]);

  useEffect(() => {
    if (triviaData.goToResult) {
      handleRefetch();
    }
  }, [handleRefetch, triviaData.goToResult]);

  const handleShowStats = useCallback(() => {
    if (isMobile) {
      // Mobile stats handler
      setPreviousStep(step);
      setPreviousTotalStats(totalStats);
      setTotalStats(true);
      setStep(2);
      setCameFromInit(true);
    } else {
      // Desktop stats handler
      setPreviousStep(step);
      setPreviousTotalStats(showStats);
      setShowStats(!showStats);
      setStep(2);
    }
  }, [isMobile, showStats, step, totalStats]);

  const comeBackTo = useCallback(() => {
    // Return to the previous step when coming back from totalStats
    if (previousStep === 2) {
      // If we were already in step 2, restore the previous totalStats state

      setStep(2);
      if (isMobile) {
        setTotalStats(previousTotalStats);
      } else {
        setShowStats(previousTotalStats);
      }
    } else if (previousStep === 0) {
      setStep(0);
      setTotalStats(false);
      setShowStats(false);
    } else if (previousStep === 3) {
      setStep(3);
      setTotalStats(false);
      setShowStats(false);
    } else {
      // Fallback logic
      if (!triviaInit?.hayTriviaPendiente && !triviaInit?.hayTriviaActiva) {
        setStep(3);
      } else {
        if (shouldShowResult) {
          setStep(2);
        } else {
          setStep(0);
        }
      }
      setShowStats(false);
      setTotalStats(false);
    }
  }, [
    previousStep,
    previousTotalStats,
    isMobile,
    shouldShowResult,
    triviaInit?.hayTriviaActiva,
    triviaInit?.hayTriviaPendiente,
    step,
    totalStats,
    showStats,
  ]);

  // Mobile back handler
  const handleBack = useCallback(() => {
    if (step === 0 || step === 3) {
      return navigate(ROUTES.summary);
    }

    if (step === 2) {
      if (totalStats) {
        if (cameFromInit) {
          setTotalStats(false);
          setCameFromInit(false);
          setStep(0);
          return;
        } else {
          setTotalStats(false);
          return;
        }
      } else {
        return navigate(ROUTES.summary);
      }
    }

    setStep(0);
  }, [step, totalStats, cameFromInit, navigate]);

  const isWinner = useMemo(() => {
    if (!triviaInit) {
      return false;
    }
    return triviaInit?.resultadoParticipacion?.cantidadDeMedallas > 0;
  }, [triviaInit]);

  return {
    step,
    showStats,
    totalStats,
    setTotalStats,
    triviaInit,
    isLoading,
    isRefetching,
    handleInitQuestions,
    handleShowStats,
    comeBackTo,
    handleBack,
    isWinner,
  };
};
